import React, { FC, useContext, useMemo, useState } from 'react'
import { orderBy } from 'lodash'
import { Charts, GridTableStatusEnum, IGridTableColumn, NewGridTable } from '@ui'
import { PrintTitle, TabContext } from '../Details'
import { IPortfolioDepartment, Order } from '@api'
import { DivProps } from '@commonTypes'
import { Classes } from '@blueprintjs/core'
import classnames from 'classnames'
import css from './seatedEmployeesByDepartment.module.scss'

type Sorts = 'seatedCount' | 'department.name'

export default function AreaByDepartment() {
  const scale = useMemo(() => Charts.colorScale(), [])
  const { object, loading } = useContext(TabContext)
  const [sort, setSort] = useState<Sorts>('seatedCount')
  const [sortAscending, setSortAscending] = useState(false)

  let departments = object?.departments
  departments = orderBy(departments, sort, sortAscending ? 'asc' : 'desc') as IPortfolioDepartment[]

  const max = departments.reduce((max, d) => Math.max(max, d.seatedCount), 0)

  const columns: IGridTableColumn<IPortfolioDepartment & { id: string }>[] = [
    {
      label: 'Department',
      sort: 'department.name',
      Cell: ({ cell, skeleton }) => {
        return (
          <div className={classnames({ [Classes.SKELETON]: skeleton })}>
            {cell ? cell.data.department?.name : 'placeholder'}
          </div>
        )
      },
    },
    {
      sort: 'seatedCount',
      label: 'Seated Count',
      Cell: ({ cell, skeleton }) => {
        if (!cell) return null

        return (
          <div data-test="seatedCount" className={classnames({ [Classes.SKELETON]: skeleton })}>
            {cell ? cell.data.seatedCount : 'plchld'}
          </div>
        )
      },
    },
    {
      label: '',
      Cell: ({ cell }) => {
        if (!cell || cell.data.id === 'total') return null

        return (
          <SimpleBar
            max={max}
            val={cell.data.seatedCount}
            barColor={`#${scale(cell.data.department?.name)}`}
          />
        )
      },
    },
  ]

  const formatDepartments = (items: IPortfolioDepartment[]) => {
    return items.map((d) => ({
      data: { id: d.department?.id, ...d },
    }))
  }

  let formatedDepartments = formatDepartments(
    orderBy(departments, sort, sortAscending ? 'asc' : 'desc') as IPortfolioDepartment[]
  )

  //todo use table total line feature (appendData, appendColumn)
  formatedDepartments = [
    ...formatedDepartments,
    ...(object
      ? [
          {
            data: {
              id: 'total',
              allocated: object.allocated,
              areaSqft: object.areaSqft,
              areaSqrm: object.areaSqft,
              occupied: object.occupied,
              seatedCount: object.seatedCount,
              unoccupied: object.unoccupied,
              department: {
                id: 'total',
                name: 'Total',
                color: '',
              },
              teams: [],
            },
          },
        ]
      : []),
  ]

  return (
    <>
      <PrintTitle>
        <h3>Seated Employees By Department</h3>
      </PrintTitle>
      <NewGridTable<IPortfolioDepartment & { id: string }>
        status={loading ? GridTableStatusEnum.LOADING : GridTableStatusEnum.IDLE}
        minCellWidth="auto"
        maxCellWidth="auto"
        columns={columns}
        data={formatedDepartments}
        sort={{
          column: sort,
          order: sortAscending ? Order.ASC : Order.DESC,
          onSort: (column: string) => {
            if (sort === column) {
              return setSortAscending(!sortAscending)
            } else {
              setSort(column as Sorts)
              setSortAscending(false)
            }
          },
        }}
      />
    </>
  )
}

type SimpleBarPropsT = DivProps & {
  val: number
  max: number
  barColor: string
  bar2Color?: string
  val2?: number
}

export const SimpleBar: FC<React.PropsWithChildren<SimpleBarPropsT>> = ({
  val,
  max,
  val2,
  barColor,
  bar2Color,
  className,
  children,
  ...props
}) => {
  return (
    <div className={classnames(css.simple_bar, className)} {...props}>
      {children}
      <span
        className={css.before}
        style={{
          backgroundColor: bar2Color,
          right: `${val2 ? (1 - val2 / max) * 100 : '100'}%`,
        }}
      />
      <span
        className={css.after}
        style={{
          backgroundColor: barColor,
          right: `${(1 - val / max) * 100}%`,
        }}
      />
    </div>
  )
}
